import React from 'react';
import { Link } from 'react-router-dom';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  Badge,
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// @ts-expect-error -- MUI styled + component prop typing issue
const StyledListItem = styled(ListItemButton)(({ theme }) => ({
  margin: '4px 8px',
  borderRadius: '8px',
  minHeight: { xs: '44px', sm: '48px' },
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    '& .MuiListItemIcon-root, & .MuiListItemText-root': {
      color: theme.palette.common.white,
    },
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '& .MuiListItemIcon-root, & .MuiListItemText-root': {
      color: theme.palette.common.white,
    },
  },
}));

export interface NavItemData {
  id: string;
  label: string;
  icon: SvgIconComponent;
  path?: string;
  search?: string;
  /** Router state the link carries (how a page learns it was opened from the nav). */
  state?: Readonly<Record<string, unknown>>;
  /** A count of things waiting there (the director's open flags); never red. */
  badge?: number;
}

interface NavigationItemProps {
  item: NavItemData;
  isSelected?: boolean;
  onClick?: () => void;
  showAsLink?: boolean;
  /** Icon-rail mode: icon only, label carried by a tooltip. */
  rail?: boolean;
}

const NavigationItem = ({
  item,
  isSelected = false,
  onClick,
  showAsLink = true,
  rail = false,
}: NavigationItemProps) => {
  const IconComponent = item.icon;

  const badge = item.badge && item.badge > 0 ? item.badge : 0;
  const itemContent = rail ? (
    <ListItemIcon sx={{ minWidth: 0 }}>
      {badge ? (
        <Badge
          badgeContent={badge}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: 'var(--bg-alt)',
              color: 'var(--ink)',
              fontWeight: 800,
            },
          }}
        >
          <IconComponent />
        </Badge>
      ) : (
        <IconComponent />
      )}
    </ListItemIcon>
  ) : (
    <>
      <ListItemIcon>
        <IconComponent />
      </ListItemIcon>
      <ListItemText primary={item.label} />
      {badge ? (
        <Box
          component="span"
          aria-label={`${badge} waiting`}
          sx={{
            ml: 1,
            minWidth: 24,
            height: 22,
            px: '7px',
            borderRadius: 'var(--radius-pill)',
            bgcolor: 'var(--bg-alt)',
            color: 'var(--ink)',
            fontSize: 12,
            fontWeight: 800,
            display: 'inline-grid',
            placeItems: 'center',
            fontFeatureSettings: "'tnum'",
          }}
        >
          {badge}
        </Box>
      ) : null}
    </>
  );

  const commonProps = {
    selected: isSelected,
    // MUI's `selected` is styling only; announce the active page too.
    ...(isSelected ? { 'aria-current': 'page' as const } : {}),
    onClick,
    ...(rail
      ? {
          'aria-label': item.label,
          sx: { justifyContent: 'center', minHeight: 48 },
        }
      : {}),
  };

  // Render as Link for navigation items (support optional search for in-app vs landing)
  const button =
    showAsLink && item.path ? (
      <StyledListItem
        {...({
          component: Link,
          to: item.search
            ? { pathname: item.path, search: item.search }
            : item.path,
          state: item.state,
        } as any)}
        {...commonProps}
      >
        {itemContent}
      </StyledListItem>
    ) : (
      // Render as button for action items
      <StyledListItem {...commonProps}>{itemContent}</StyledListItem>
    );

  if (!rail) return button;

  return (
    <Tooltip title={item.label} placement="right">
      {button}
    </Tooltip>
  );
};

export default NavigationItem;
